import { CurrencyPipe } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChange,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';
import { size } from 'lodash';
import { EMPTY, Subscription, timer } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
} from 'rxjs/operators';

import { AppState, OPERATION_RESET } from 'src/app';
import { config } from 'src/app/environment-config';
import { FieldSort } from 'src/components/shared/utility/field-sort';
import { BootstrapService, TradeService } from 'src/core';
import { OrderByPipe } from 'src/shared/pipes';
const env = config();
const { timeOuts_timeOut30Seg } = env;

@Component({
  selector: 'hb-blotter-funds',
  templateUrl: './blotterFunds.component.html',
})
export class BlotterFundsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() showDownloadExcel: boolean;
  @Input() isRefreshing: boolean;
  public showSkeleton = false;
  public ordersEmptyState: any[] = [1, 2, 3];

  bootstrapPromise: Promise<any>;
  orders: any[] = [];
  ordersLenght: number;
  totalRows = 1;

  blotterFundsSubscription: Subscription;
  sorting: any = {
    prop: 'processDate',
    reverse: true,
  };

  private currentContract: string;
  private intervalTimeOut: number = +timeOuts_timeOut30Seg;

  private orderByPipe: OrderByPipe = new OrderByPipe();
  private source: any[] = [];
  private filter = '';
  private blotterFilterSubscription: Subscription;
  private currentContractSubscription: Subscription;
  private currentFundsSubscription: Subscription;
  private intervalFundsDataSubscription: Subscription;

  private seekChangeSubscription: Subscription;
  private sort: FieldSort = new FieldSort();

  constructor(
    private store: Store<AppState>,
    private bootstrapService: BootstrapService,
    private tradeService: TradeService,
  ) {}

  ngOnInit() {
    this.bootstrapPromise = this.bootstrapService.initSession().then(() => {
      this.currentContractSubscription = this.store
        .select('currentContract')
        .pipe(
          map((values: any) => values.get('contractID')),
          filter((contractId: any) => contractId !== undefined),
          distinctUntilChanged(),
        )
        .subscribe((contractId: any) => {
          this.showSkeleton = true;
          this.currentContract = contractId;
          this.orders = [];
          this.getOrders();
        });

      this.currentFundsSubscription = this.store
        .select('blotter')
        .pipe(
          distinctUntilChanged(),
          map((values: any) => {
            return values.get('funds') !== undefined
              ? values.get('funds').toJS()
              : [];
          }),
        )
        // .filter((data: any[]) => data.length > 0)
        .subscribe((data: any[]) => {
          this.source = data;
          this.totalRows = data.length > 0 ? data.length : 0;
          this.filterData('');
        });

      this.blotterFilterSubscription = this.store
        .select('blotter')
        .pipe(
          map((values: any) => values.get('filter')),
          filter((data: any) => data !== undefined),
          distinctUntilChanged(),
        )
        .subscribe((filterShadowed: any) => {
          this.filter = filterShadowed;
          this.filterData(this.filter);
        });

      this.seekChangeSubscription = this.store
        .select('operation')
        .pipe(
          map((value: any) => value.toJS()),
          distinctUntilChanged(),
        )
        .subscribe((value: any) => {
          if (value.reset) {
            this.getOrders();

            this.store.dispatch({
              type: OPERATION_RESET,
              payload: { reset: false },
            });
          }
        });
    });
  }

  ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
    const { isRefreshing } = changes;
    if (isRefreshing && isRefreshing.currentValue !== undefined) {
      if (isRefreshing.currentValue) {
        this.showSkeleton = true;
      } else {
        this.showSkeleton = false;
      }
    }
  }

  ngOnDestroy() {
    [
      this.seekChangeSubscription,
      this.currentContractSubscription,
      this.currentFundsSubscription,
      this.blotterFilterSubscription,
      this.intervalFundsDataSubscription,
    ]
      .filter((subscription) => !!subscription)
      .forEach((subscription) => subscription.unsubscribe());
  }

  setSortingProp(property: string): void {
    if (!property || property === '') {
      return;
    }

    if (this.sorting.prop === property) {
      this.sorting.reverse = !this.sorting.reverse;
    } else {
      this.sorting.prop = property;
      this.sorting.reverse = true;
    }

    this.sortData();
  }

  generateExcel(headers?: object): void {
    let generatedFile;
    const date = new Date().getTime();
    const length = size(this.orders);
    const exportObject = [];
    const fileHeaders = {
      fund: 'Fondo',
      hour: 'Hora',
      settlementDate: 'Fecha de liquidación',
      originalQuantity: 'Títulos',
      price: 'Precio',
      folio: 'Folio',
      way: 'Sentido',
      total: 'Total',
    };
    exportObject.push(fileHeaders);

    for (let i = 0; i < length; i += 1) {
      const order = this.orders[i];
      const orderData: any = {
        fund: order.fund,
        hour: order.hour,
        settlementDate: order.settlementDate,
        originalQuantity: order.originalQuantity,
        price: new CurrencyPipe('es-MX').transform(
          order.price,
          'MXN',
          'symbol-narrow',
          '1.2-2',
        ),
        folio: order.folio,
        way: order.way,
        total: new CurrencyPipe('es-MX').transform(
          order.total,
          'MXN',
          'symbol-narrow',
          '1.2-2',
        ),
      };
      exportObject.push(orderData);
    }
    generatedFile = new AngularCsv(
      exportObject,
      `GBM_Homebroker_Ordenes_Fondos_${date}`,
    );
  }

  reorderST(property: string): void {
    this.orders = this.sort.SortField(property, this.orders);
  }

  public filterData(filterText: string): void {
    if (filterText.length > 0) {
      this.orders = this.source.filter((item: any) => {
        const searchStr: string = item.fund.toLowerCase();
        return searchStr.indexOf(filterText.toLowerCase()) !== -1;
      });
      return;
    }
    this.orders = [];
    this.orders = this.source;
    this.ordersLenght = size(this.orders);
    this.sortData();
  }

  private sortData(): void {
    this.orders = this.orderByPipe.transform(
      this.orders,
      this.getConvertSortingProp(),
    );
  }

  private getOrders(isTimer = false): void {
    this.intervalFundsDataSubscription &&
      this.intervalFundsDataSubscription.unsubscribe();

    this.intervalFundsDataSubscription = timer(0, this.intervalTimeOut)
      .pipe(
        mergeMap((period) =>
          this.tradeService
            .getFundsOrders(this.currentContract, isTimer || !!period)
            .pipe(catchError(() => EMPTY)),
        ),
      )
      .subscribe(() => (this.showSkeleton = false));
  }

  private getConvertSortingProp(): string {
    return this.sorting.reverse ? `-${this.sorting.prop}` : this.sorting.prop;
  }
}
